import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, X, ArrowLeft, Users, Building2, Layers, Settings,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import PermissionCheckboxes from '../components/admin/PermissionCheckboxes';
import ResultCount from '../components/shared/ResultCount';
import SortableHeader, { usePagedSortableData, TablePager } from '../components/SortableTable';
import { fetchJSON } from '../utils/api';
import { inputCls, InfoTip, LockTip } from '../components/shared/FormKit';

const THEAD_CLS = 'px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider';

// Roles & default permissions (docs/modules/auth-admin.md §5). An account
// carries one or more roles and holds their permissions live — saving a role
// here changes every account carrying it at once, so the editor says how many
// that is.
//
// Roles sit on four tabs by roles.category: General (exactly one role — the
// catch-all new accounts fall back to when their department names no role of
// its own), Departments (one category per staff department, holding that
// department's roles), Teams (functional add-ons) and System (Admin, locked:
// its '*' default is part of the permission mechanics).
//
// 'Default for new accounts' marks the one role a department's self-created
// accounts start with. Only a department role can hold it, and only for its
// own department, so the tick is a plain yes/no: which department it means is
// already the row's own department.
// 'Designations' narrows that per designation: a department role can list
// designations of its own department, and a self-created account whose
// department + designation is listed starts with that role instead. Each
// designation within a department sits on one role at most, and the
// department default role itself carries none. Order at registration:
// designation match → department default → General.
// Admin-only keys (registry `adminOnly`, e.g. admin:settings) never appear in
// the tick-list — they come with the Admin role's '*' alone and the backend
// rejects them in role defaults and account extras.

const TABS = [
  { id: 'Departments', label: 'Departments', icon: Building2, hint: 'One group per staff department, holding that department’s roles. An account can carry any number of them.' },
  { id: 'Teams', label: 'Teams', icon: Layers, hint: 'Functional add-ons carried on top of a department role, for example the QM or L&D team.' },
  { id: 'General', label: 'General', icon: Users, hint: 'The default permissions a new account gets when it is created without a department role of its own.' },
  { id: 'System', label: 'System', icon: Settings, hint: 'Admin holds every permission and cannot be changed here.' },
];

// Which tab a role belongs to. Anything that is not a fixed category is a department.
const tabOf = (role) => {
  const c = role.category || 'Teams';
  if (c === 'General' || c === 'Teams' || c === 'System') return c;
  return 'Departments';
};

const SettingsRoles = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('admin:users');

  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('Departments');

  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, p] = await Promise.all([
        fetchJSON('/api/admin/roles'),
        fetchJSON('/api/admin/permissions'),
      ]);
      setRoles(r.roles || []);
      setDepartments(r.departments || []);
      setDesignations(r.designations || []);
      setAllPermissions(p.permissions || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!canManage) return;
    load();
  }, [canManage, load]);

  const visible = useMemo(() => roles.filter(r => tabOf(r) === tab), [roles, tab]);
  const { rows: rolePage, sortConfig, requestSort, page, setPage, total } = usePagedSortableData(visible);

  if (!canManage) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-red-800">Admin access required</h2>
          <p className="text-sm text-red-600 mt-1">You need the admin:users permission to manage roles.</p>
        </div>
      </div>
    );
  }

  const defaultsLabel = (role) => {
    if (role.permissions === '*') return 'All permissions';
    const n = Array.isArray(role.permissions) ? role.permissions.length : 0;
    return `${n} permission${n === 1 ? '' : 's'}`;
  };

  const generalRole = roles.find(r => r.category === 'General') || null;
  // department → the role its new accounts default to (exactly one).
  const departmentOwner = {};
  roles.forEach(r => (r.departments || []).forEach(d => { departmentOwner[d] = r; }));
  // department → designation → the role those accounts default to.
  const designationOwner = {};
  roles.forEach(r => (r.designations || []).forEach(g => {
    designationOwner[r.category] = { ...(designationOwner[r.category] || {}), [g]: r };
  }));
  const current = TABS.find(t => t.id === tab) || TABS[0];
  // General exists once and System is seeded — only department and team roles are created here.
  const canCreate = tab === 'Departments' || tab === 'Teams';
  const isDepartments = tab === 'Departments';

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <Link to="/manage" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="w-4 h-4" />Management</Link>
      <div className="mt-2 mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Roles & Default Permissions</h1>
          <p className="text-sm text-slate-500 mt-1">
            Accounts hold their roles' permissions live — changing a role here changes every account that carries it, immediately.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ResultCount count={visible.length} noun="role" loading={loading} />
          {canCreate && (
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" /> {isDepartments ? 'New Department Role' : 'New Team Role'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm mb-4">{error}</div>
      )}

      <div className="border-b border-slate-200 mb-3 flex flex-wrap gap-1">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = t.id === tab;
          const n = roles.filter(r => tabOf(r) === t.id).length;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => { setTab(t.id); setPage(1); }}
              aria-current={active ? 'page' : undefined}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
              <span className={`ml-0.5 text-xs font-semibold ${active ? 'text-blue-500' : 'text-slate-400'}`}>{n}</span>
            </button>
          );
        })}
      </div>
      <p className="text-sm text-slate-500 mb-4">{current.hint}</p>

      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="table-scroll">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {isDepartments && (
                    <SortableHeader label="Department" sortKey="category" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                  )}
                  <SortableHeader label="Role" sortKey="name" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                  <SortableHeader label="Permissions" sortKey="permissions" sortConfig={sortConfig} onSort={requestSort} accessor={r => (r.permissions?.length || 0)} className={THEAD_CLS} />
                  {(isDepartments || tab === 'General') && (
                    <SortableHeader label="Default for New Accounts" sortKey="departments" sortConfig={sortConfig} onSort={requestSort} accessor={r => (r.departments?.length || 0)} className={THEAD_CLS} />
                  )}
                  {isDepartments && (
                    <SortableHeader label="Designations" sortKey="designations" sortConfig={sortConfig} onSort={requestSort} accessor={r => (r.designations?.length || 0)} className={THEAD_CLS} />
                  )}
                  <SortableHeader label="Users" sortKey="user_count" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                  <SortableHeader label="Actions" sortable={false} align="right" className={THEAD_CLS} />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {rolePage.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-slate-500">No roles on this tab yet</td>
                  </tr>
                )}
                {rolePage.map((r, i) => (
                  <tr key={r.key} className="hover:bg-slate-50">
                    {isDepartments && (
                      <td className="px-4 py-2.5 text-xs">
                        {/* Name the department once per run of rows; the run reads as one block. */}
                        {i === 0 || rolePage[i - 1].category !== r.category ? (
                          <span className="font-semibold text-slate-600 uppercase tracking-wider">{r.category}</span>
                        ) : null}
                      </td>
                    )}
                    <td className="px-4 py-2.5">
                      <span className="font-medium text-slate-900">{r.name}</span>
                      {r.is_system && (
                        <span className="ml-2 px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-xs">
                          System
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{defaultsLabel(r)}</td>
                    {/* A marker, not a department name: a role can only take its own
                        department's new accounts, so the name would repeat the row.
                        The General role is always marked — it is the fallback for
                        departments that name no role. */}
                    {(isDepartments || tab === 'General') && (
                      <td className="px-4 py-2.5 text-slate-500 text-xs">
                        {r.category === 'General' || r.departments?.length > 0 ? (
                          <span
                            className="font-medium text-blue-600"
                            title={r.category === 'General'
                              ? 'New accounts from any department without a role of its own start with this role'
                              : `New ${r.category} accounts start with this role`}
                          >
                            Default
                          </span>
                        ) : (
                          <span className="text-slate-400" aria-label="No">—</span>
                        )}
                      </td>
                    )}
                    {isDepartments && (
                      <td className="px-4 py-2.5 text-slate-500 text-xs max-w-xs">
                        <DesignationSummary designations={r.designations} department={r.category} />
                      </td>
                    )}
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{r.user_count}</td>
                    <td className="px-4 py-2.5 text-right">
                      {r.is_system ? (
                        <LockTip text="System role — cannot be renamed, edited or deleted" align="right">
                          <span className="inline-flex gap-1 text-slate-300">
                            <span className="p-1.5"><Pencil className="w-4 h-4" /></span>
                            <span className="p-1.5"><Trash2 className="w-4 h-4" /></span>
                          </span>
                        </LockTip>
                      ) : (
                        <div className="inline-flex gap-1">
                          <button
                            onClick={() => setEditing(r)}
                            className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          {r.category === 'General' ? (
                            <LockTip text="New accounts fall back to this role — it cannot be deleted" align="right">
                              <span className="p-1.5 text-slate-300"><Trash2 className="w-4 h-4" /></span>
                            </LockTip>
                          ) : (
                            <button
                              onClick={() => setDeleting(r)}
                              className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TablePager
            page={page}
            setPage={setPage}
            total={total}
            className="flex items-center justify-between px-4 py-2.5 border-t border-slate-200 bg-slate-50"
          />
        </div>
      )}

      {showCreate && (
        <RoleModal
          kind={isDepartments ? 'Department' : 'Teams'}
          allPermissions={allPermissions}
          departments={departments}
          departmentOwner={departmentOwner}
          designations={designations}
          designationOwner={designationOwner}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
      {editing && (
        <RoleModal
          role={editing}
          kind={tabOf(editing) === 'Departments' ? 'Department' : tabOf(editing)}
          allPermissions={allPermissions}
          departments={departments}
          departmentOwner={departmentOwner}
          designations={designations}
          designationOwner={designationOwner}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {deleting && (
        <DeleteRoleModal
          role={deleting}
          roles={roles}
          generalRole={generalRole}
          onClose={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); load(); }}
        />
      )}
    </div>
  );
};

// Create (no `role` prop) / edit (with `role`) share one modal. `kind` fixes
// what the role is — General / Department / Teams — and with it which
// controls appear: only a department role picks its department and can be the
// default for that department's new accounts, whole or per designation.
const RoleModal = ({ role, kind, allPermissions, departments, departmentOwner, designations, designationOwner, onClose, onSaved }) => {
  const [name, setName] = useState(role ? role.name : '');
  const [department, setDepartment] = useState(
    kind === 'Department' ? (role ? role.category : (departments[0] || '')) : ''
  );
  const [isDefault, setIsDefault] = useState(!!(role && (role.departments || []).length > 0));
  const [pickedDesignations, setPickedDesignations] = useState(role ? (role.designations || []) : []);
  const [selected, setSelected] = useState(role && Array.isArray(role.permissions) ? role.permissions : []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const category = kind === 'Department' ? department : kind;
  // Another role already takes this department's registrations — untick it there first.
  const owner = kind === 'Department' && department ? departmentOwner[department] : null;
  const heldByOther = !!(owner && (!role || owner.key !== role.key));
  // Designations of this department another role already lists, designation → role name.
  // Picks carried over from a department switch are dropped when they clash.
  const designationsTaken = {};
  Object.entries((kind === 'Department' && designationOwner[department]) || {}).forEach(([g, r]) => {
    if (!role || r.key !== role.key) designationsTaken[g] = r.name;
  });
  const designationsToSave = pickedDesignations.filter(g => !designationsTaken[g]);
  // The department default already takes every account not listed elsewhere,
  // so it carries no designations (backend-enforced); the picks stay in state
  // in case the tick comes off again before saving.
  const isDepartmentDefault = kind === 'Department' && isDefault && !heldByOther;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = JSON.stringify({
        name,
        category,
        permissions: selected,
        departments: isDepartmentDefault ? [department] : [],
        designations: kind === 'Department' && !isDepartmentDefault ? designationsToSave : [],
      });
      if (role) {
        await fetchJSON(`/api/admin/roles/${role.key}`, { method: 'PATCH', body });
      } else {
        await fetchJSON('/api/admin/roles', { method: 'POST', body });
      }
      onSaved();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  const title = role
    ? `Edit ${role.name}`
    : (kind === 'Department' ? 'Create Department Role' : 'Create Team Role');

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      {/* 3xl (not 2xl): the permission list is one row per key — labels must fit on one line. */}
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          {kind === 'General' && (
            <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2">
              This is the General role: the default permissions a new account gets when it is created
              without a department role of its own. There is exactly one, so it cannot move to another
              tab or be deleted.
            </p>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Name<span className="text-red-500 ml-0.5">*</span>
            </label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} className={inputCls} />
          </div>
          {kind === 'Department' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Department<span className="text-red-500 ml-0.5">*</span>
                <InfoTip
                  className="ml-1.5"
                  text="The department this role belongs to — it is listed under that department here and in the Accounts role picker."
                />
              </label>
              <select value={department} onChange={e => setDepartment(e.target.value)} className={inputCls}>
                {departments.length === 0 && <option value="">No departments in the staff master yet</option>}
                {departments.map(d => <option key={d} value={d}>{d}</option>)}
                {department && !departments.includes(department) && <option value={department}>{department}</option>}
              </select>
              <label className={`mt-2 flex items-start gap-2 text-sm ${heldByOther ? 'text-slate-400' : 'text-slate-700'}`}>
                <input
                  type="checkbox"
                  checked={isDefault && !heldByOther}
                  disabled={heldByOther || !department}
                  onChange={() => setIsDefault(v => !v)}
                  className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:opacity-60"
                />
                <span>
                  New {department || 'this department'} accounts start with this role
                  <span className="block text-xs text-slate-400">
                    {heldByOther
                      ? `Currently ${owner.name} is that default — untick it there first.`
                      : 'Applies when someone from this department creates their own account and their designation is not listed under another role.'}
                  </span>
                </span>
              </label>
            </div>
          )}
          {kind === 'Department' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Designations
                <InfoTip
                  className="ml-1.5"
                  text="New accounts from this department with a ticked designation start with this role, ahead of the department default above. Designations not ticked on any role follow the department default."
                />
              </label>
              {isDepartmentDefault ? (
                <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2">
                  Not needed — this role is the {department} default, so it already takes every {department} account
                  whose designation is not listed under another role.
                  {designationsToSave.length > 0 && ' Saving clears the designations ticked here.'}
                </p>
              ) : (
                <DesignationCheckboxes
                  designations={designations}
                  selected={designationsToSave}
                  taken={designationsTaken}
                  onChange={setPickedDesignations}
                  department={department}
                />
              )}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Permissions This Role Gives</label>
            {role && role.user_count > 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-2">
                {role.user_count} account{role.user_count === 1 ? ' carries' : 's carry'} this role — saving changes
                {role.user_count === 1 ? ' its' : ' their'} permissions immediately.
              </p>
            )}
            {/* Admin-only keys stay out of role defaults (backend-enforced) — the Admin role's '*' is their only source. */}
            <PermissionCheckboxes
              allPermissions={allPermissions}
              selected={selected}
              onChange={setSelected}
            />
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
            <button
              onClick={save}
              disabled={saving || !name.trim() || (kind === 'Department' && !department)}
              className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// The Designations cell of the role list: the names while they fit, a count
// past three (full list in the tooltip).
const DesignationSummary = ({ designations, department }) => {
  const list = designations || [];
  if (list.length === 0) return <span className="text-slate-400" aria-label="None">—</span>;
  const tip = `New ${department} accounts with these designations start with this role: ${list.join(', ')}`;
  if (list.length <= 3) return <span title={tip}>{list.join(', ')}</span>;
  return (
    <span title={tip}>
      {list.slice(0, 2).join(', ')} <span className="text-slate-400">+{list.length - 2} more</span>
    </span>
  );
};

// Designation tick-list for a department role: one flat list in the master
// list's order, no grade sections. `taken` (designation → role name) marks
// the ones another role of the same department already lists: shown disabled
// with that role's name, never part of `selected`.
const DesignationCheckboxes = ({ designations, selected, taken, onChange, department }) => {
  const values = useMemo(() => {
    const active = designations.map(d => d.value);
    // Ticked designations since deactivated in the master list stay at the end so they can be unticked.
    const activeSet = new Set(active);
    return [...active, ...selected.filter(v => !activeSet.has(v))];
  }, [designations, selected]);

  const toggle = (value) => {
    const set = new Set(selected);
    if (set.has(value)) set.delete(value); else set.add(value);
    onChange([...set]);
  };

  if (values.length === 0) {
    return <p className="text-xs text-slate-500">No designations in the staff master yet.</p>;
  }

  return (
    <div className="border border-slate-200 rounded-lg bg-white">
      <div className="flex items-center gap-3 px-3 py-2 border-b border-slate-200 bg-slate-50 rounded-t-lg">
        <span className="flex-1 text-xs text-slate-500">
          New {department || 'department'} accounts with a ticked designation start with this role.
        </span>
        <span className="text-xs text-slate-500 whitespace-nowrap">
          <span className="font-semibold text-slate-700">{selected.length}</span> selected
        </span>
        {selected.length > 0 && (
          <button type="button" onClick={() => onChange([])} className="text-xs text-slate-500 hover:text-slate-700 whitespace-nowrap">
            Clear All
          </button>
        )}
      </div>
      <div className="max-h-64 overflow-y-auto p-2 grid grid-cols-1 sm:grid-cols-2 gap-x-2 gap-y-0.5 content-start">
        {values.map(value => {
          const takenBy = taken[value];
          const checked = !takenBy && selected.includes(value);
          return (
            <LockTip key={value} text={takenBy ? `Listed under ${takenBy} — untick it there first` : null} block>
            <label
              key={value}
              className={`flex items-start gap-2 px-2 py-1.5 rounded-lg border text-sm ${
                takenBy ? 'border-transparent text-slate-400'
                  : checked ? 'border-blue-200 bg-blue-50/70 text-blue-900 cursor-pointer' : 'border-transparent text-slate-800 hover:bg-slate-50 cursor-pointer'
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={!!takenBy}
                onChange={() => toggle(value)}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 shrink-0 disabled:opacity-60"
              />
              <span className="min-w-0 leading-5">
                {value}
                {takenBy && <span className="block text-xs text-slate-400">{takenBy}</span>}
              </span>
            </label>
            </LockTip>
          );
        })}
      </div>
    </div>
  );
};

// In-app confirm for deleting a role (replaces window.confirm). A role that
// accounts still carry can go too: pick the role they get instead and both
// happen in one call (DELETE ?transfer_to=). Each account keeps its other
// roles and its extras; what this role gave is gone unless the new role gives
// it too. The General role never reaches here (no delete button).
const DeleteRoleModal = ({ role, roles, generalRole, onClose, onDeleted }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [transferTo, setTransferTo] = useState('');
  const needsTransfer = role.user_count > 0;
  const targets = roles.filter(r => r.key !== role.key && !r.is_system);
  const blocked = needsTransfer && !transferTo;

  const confirmDelete = async () => {
    setBusy(true); setError(null);
    try {
      const qs = needsTransfer ? `?transfer_to=${encodeURIComponent(transferTo)}` : '';
      await fetchJSON(`/api/admin/roles/${role.key}${qs}`, { method: 'DELETE' });
      onDeleted();
    } catch (e) { setError(e.message); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Delete Role</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-700">
            Delete <span className="font-semibold">{role.name}</span>?
            {needsTransfer
              ? ` ${role.user_count} account${role.user_count === 1 ? '' : 's'} still carr${role.user_count === 1 ? 'ies' : 'y'} this role — choose the role ${role.user_count === 1 ? 'it gets' : 'they get'} instead.`
              : ' No account carries it; only the role and its permissions are removed.'}
          </p>
          {needsTransfer && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Give {role.user_count === 1 ? 'this account' : 'these accounts'} the role
              </label>
              <select
                value={transferTo}
                onChange={e => setTransferTo(e.target.value)}
                className={inputCls}
                disabled={busy}
              >
                <option value="">Select a role…</option>
                {targets.map(r => (
                  <option key={r.key} value={r.key}>{r.name}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Each account keeps its other roles and its extras. What {role.name} gave is gone unless the new role gives it too.
              </p>
            </div>
          )}
          {role.departments && role.departments.length > 0 && (
            <p className="text-sm text-slate-700">
              New {role.departments.join(', ')} accounts start with this role today; they would start with {generalRole ? generalRole.name : 'the General role'} instead.
            </p>
          )}
          {role.designations && role.designations.length > 0 && (
            <p className="text-sm text-slate-700">
              New {role.category} accounts with {role.designations.length === 1 ? 'the designation' : 'the designations'} {role.designations.join(', ')} start
              with this role today; they would follow the {role.category} default instead.
            </p>
          )}
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} disabled={busy} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded disabled:opacity-50">Cancel</button>
            <button onClick={confirmDelete} disabled={busy || blocked} className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50">
              {busy ? (needsTransfer ? 'Moving & Deleting…' : 'Deleting…') : (needsTransfer ? 'Move Accounts & Delete' : 'Delete')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsRoles;
